import React, { useState } from 'react';
import { fmtDate, fmtDay, fmtMoney, statusTag } from '../lib/format';
import { IconChevron, IconMail, IconMessage, IconPhone, IconPlus } from './PortalIcons';
import SwipeToDelete from './SwipeToDelete';
import { orderEstimates, moveTo } from '../lib/estimateOrder';
import ProjectStatement from './ProjectStatement';

// A client's own page, laid out like Joist's Profile (built 2026-08-18 from
// Julian's screenshot): the name, the ways to reach them and the address in
// cards at the top, then Customer Snapshots — total invoiced, still owed, last
// invoice, last payment — then their estimates and their invoices.
//
// Tapping a client in the Clients tab used to do nothing at all. Everything
// about a repeat customer was spread across the Leads pipeline, the Projects
// tab and the ledger, and answering "what does this one owe me?" meant reading
// three screens and adding it up. This is that answer on one screen.
//
// The money is worked out here rather than fetched: the admin dashboard has
// already loaded every project, request and estimate, and a client's figures
// are a filter and a sum away. Nothing new is asked of the server.

// What the homeowner has been billed lives on a project's lines, and what they
// have paid on its payments — the client-facing half of the Project Statement.
// A request's own `invoice` is the TECH billing QPS, so it has no place here.
const sum = (rows, pick) => (rows || []).reduce((t, r) => t + (Number(pick(r)) || 0), 0);
// A discount and the tax are statement lines like any other, because that is
// what keeps every total in the app agreeing. They are money rather than work,
// so they are not counted among the items and they read under the total.
const isMoneyLine = (l) => ['discount', 'tax'].includes((l.trade || '').trim().toLowerCase());

const latest = (dates) => {
  const real = dates.filter(Boolean).map(d => new Date(d)).filter(d => !isNaN(d));
  return real.length ? new Date(Math.max(...real)) : null;
};

// Two or three figures across one card. It was fixed at two, which is why
// the money row could say what this client had been billed and what was
// still outstanding but never what had actually come in.
const Snapshot = ({ cells }) => (
  <div className="cp-card cp-snap">
    {cells.filter(Boolean).map((cell, i) => (
      <div className="cp-snap-cell" key={i}>
        <div className="cp-snap-val" style={cell.tone ? { color: cell.tone } : undefined}>{cell.value}</div>
        <div className="cp-snap-lbl">{cell.label}</div>
      </div>
    ))}
  </div>
);

export default function ClientProfile({
  client, projects, requests, onBack, onEdit, onAddLead, onOpenEstimate, onOpenProject,
  onCreateEstimate, onCreateInvoice, onInvoiceEstimate, onPrintInvoice,
  onOpenJobInvoice,
  onDeleteEstimate, onDeleteInvoice,
  // Saves the order Julian dragged a client's estimates into.
  onReorderEstimates,
  // Click a description or a number on a statement and read that bill.
  onOpenInvoice,
  // Turning this client into a commercial account. Offered, never done on
  // its own: whether somebody is a homeowner with two houses or a company
  // with two hundred doors is not a thing to work out from an address count.
  onPromote,
}) {
  const [extra, setExtra] = useState(false);
  const [drill, setDrill] = useState(null);   // 'estimates' | 'invoices'
  const [openInvoice, setOpenInvoice] = useState(null);   // one invoice, read as a document

  // ── Dragging an estimate up the list ──
  //
  // Three pieces, and they are separate on purpose.
  //
  //   `armed` is the row whose grip is being held. ONLY that row is
  //   draggable, so a slide-for-Delete and a drag-to-reorder never fight
  //   over the same gesture, and letting go of the grip without dragging
  //   leaves nothing armed behind it. Arming every row on a press is how a
  //   stray drag anywhere on the list silently reorders it later.
  //
  //   `dragFrom` is set when a drag actually begins, and is what the drop
  //   reads. It cannot be `armed`, because the browser fires pointercancel
  //   on the grip the moment a drag starts — clearing the wrong one there
  //   would lose the row halfway through carrying it.
  //
  //   `dragOver` is only ever a highlight.
  const [armed, setArmed] = useState(null);
  const [dragFrom, setDragFrom] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [helping, setHelping] = useState(false);

  // A lead taken down by hand for a repeat customer isn't always linked to
  // their account, so the email is a second way in. Without one it is the link
  // or nothing — a name is not an identity.
  const email = (client.email || '').toLowerCase();
  const mine = (rec) => {
    const owner = rec.homeownerId?._id || rec.homeownerId;
    if (owner && String(owner) === String(client._id)) return true;
    return Boolean(email) && (rec.email || '').toLowerCase() === email;
  };

  const theirProjects = (projects || []).filter(mine);
  const theirRequests = (requests || []).filter(mine);

  const lines = theirProjects.flatMap(p => (p.lines || []).map(l => ({ ...l, project: p })));
  const payments = theirProjects.flatMap(p => p.payments || []);

  // ── THE OTHER HALF OF THE MONEY ──────────────────────────────────────────
  //
  // There are two places a bill can live in this app. A PROJECT carries lines,
  // which is how Julian bills a management company. A JOB carries one invoice,
  // which is how a technician closes out a driveway and how every lead on the
  // board gets billed.
  //
  // This page only ever read the project half. Sam Skanchy's job was invoiced
  // through the technician portal and his page said Total invoiced $0.00,
  // Invoices 0, last invoice sent "––". The bill existed, the customer could
  // pay it, and the one screen you would open to check said nothing had
  // happened. Both halves count now.
  // What ONE job has actually taken. The same rule the server works to: a
  // failed bank payment never arrived, a lost chargeback went back, and a
  // refund that settled is money this client no longer owes us.
  const takenOn = (r) => {
    const online = (r.onlinePayments || [])
      .filter(p => p.status === 'succeeded' && p.dispute?.status !== 'lost')
      .reduce((t, p) => t + (Number(p.amount) || 0), 0);
    const offline = (r.offlinePayments || []).reduce((t, p) => t + (Number(p.amount) || 0), 0);
    const back = (r.refunds || [])
      .filter(f => f.status === 'succeeded')
      .reduce((t, f) => t + (Number(f.amount) || 0), 0);
    return online + offline - back;
  };

  const jobBills = theirRequests
    .filter(r => r.invoice?.amount != null)
    .map(r => ({
      key: `job-${r._id}`,
      kind: 'job',
      request: r,
      number: r.workOrder?.number || '',
      date: r.invoice.submittedAt || r.createdAt,
      title: r.serviceType || 'Job',
      items: (r.invoice.lineItems || []).length || 1,
      total: Number(r.invoice.amount) || 0,
      status: r.invoiceStatus === 'paid' ? 'paid' : r.status,
      paid: takenOn(r),
      owed: Math.max(0, (Number(r.invoice.amount) || 0) - takenOn(r)),
    }))
    .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  const jobTaken = sum(theirRequests, takenOn);

  // Every distinct street they have had work at. The street line only: the
  // same building typed with and without "Ste 2" is one address, and a
  // second city on the end would make it two.
  const street = (v) => String(v || '').split(',')[0].trim().toLowerCase();
  const addresses = [...new Set([
    ...theirRequests.map(r => street(r.serviceAddress)),
    ...theirProjects.map(p => street(p.address)),
  ].filter(Boolean))];

  const jobPaymentDates = theirRequests.flatMap(r => [
    ...(r.onlinePayments || []).filter(p => p.status === 'succeeded').map(p => p.paidAt || p.createdAt),
    ...(r.offlinePayments || []).map(p => p.receivedAt),
  ]);

  const invoiced = sum(lines, l => l.amount) + sum(jobBills, b => b.total);
  const received = sum(payments, p => p.amount) + jobTaken;
  const lastInvoice = latest([...lines.map(l => l.invoiceDate), ...jobBills.map(b => b.date)]);
  const lastPayment = latest([...payments.map(p => p.date), ...jobPaymentDates]);

  // An estimate is a price that was sent. A job still being written up has no
  // date on it and nothing to show, so it doesn't count as one.
  // Sorted in lib/estimateOrder.js, which is tested. It used to sort newest
  // first right here, and Julie Tait's four estimates were all written on the
  // same day — so the sort had nothing to tell them apart and the list
  // rearranged itself every time Julian opened one and came back.
  const estimates = orderEstimates(
    theirRequests.filter(r => r.estimate?.submittedAt || (r.estimateStatus && r.estimateStatus !== 'none')),
  );

  // Hand the whole ordered list of ids to the server. A position only means
  // something against the others, so writing one row would leave the rest
  // describing an order that no longer exists.
  //
  // Nothing is drawn from local state afterwards: the save reloads the
  // requests, and the list redraws from what came back. A row that appeared
  // to move and then sprang back is worse than one that took a moment.
  const save = (ids) => { if (onReorderEstimates) onReorderEstimates(ids); };

  const drop = (to) => {
    const from = dragFrom;
    setDragFrom(null);
    setDragOver(null);
    if (from === null || from === to) return;
    save(moveTo(estimates, from, to));
  };

  // An invoice is every line raised under one number, on one project — which
  // is how it was written and how the homeowner reads it. Listing the lines
  // instead split one bill into a row per item, so a two-item invoice looked
  // like two invoices for the same money.
  const invoices = Object.values(lines.reduce((acc, l) => {
    const key = `${l.project._id}|${l.invoiceNumber || l._id}`;
    if (!acc[key]) {
      acc[key] = {
        key,
        number: l.invoiceNumber || '',
        date: l.invoiceDate,
        project: l.project,
        lines: [],
        items: 0,
        total: 0,
        paid: 0,
        owed: 0,
        settled: true,
      };
    }
    acc[key].lines.push(l);
    if (!isMoneyLine(l)) acc[key].items += 1;
    acc[key].total += Number(l.amount) || 0;
    // What has come in against this bill. The server decorates every line
    // with its own paid and owed; a project loaded without them leaves
    // `settled` false and the row says nothing rather than guessing an
    // invoice is unpaid when it is not.
    if (typeof l.paid === 'number' && typeof l.owed === 'number') {
      acc[key].paid += l.paid;
      acc[key].owed += l.owed;
    } else {
      acc[key].settled = false;
    }
    // The invoice is dated by the earliest line on it; they are written in one
    // go, but a line added later must not move the date the bill went out.
    if (l.invoiceDate && (!acc[key].date || new Date(l.invoiceDate) < new Date(acc[key].date))) {
      acc[key].date = l.invoiceDate;
    }
    return acc;
  }, {}))
    .map(inv => ({ ...inv, kind: 'project' }))
    .concat(jobBills)
    .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  // An estimate that has already been billed must not be billed again, so the
  // Invoice button is replaced by the invoice it became. The lines carry the
  // job they came from, which is the same fact the server refuses on.
  const invoicedRequestIds = new Set([
    ...lines.filter(l => l.requestId).map(l => String(l.requestId?._id || l.requestId)),
    // And one billed on the job itself, which is what the technician portal
    // does. Without this the estimate still offered an Invoice button for a
    // job that already had one.
    ...jobBills.map(b => String(b.request._id)),
  ]);

  const serviceAddress = [client.address, client.address2].filter(Boolean).join(', ');
  const serviceCityLine = [client.city, client.state, client.zip].filter(Boolean).join(', ');
  const billingLines = [
    [client.billingAddress, client.billingAddress2].filter(Boolean).join(', '),
    [client.billingCity, client.billingState, client.billingZip].filter(Boolean).join(', '),
  ].filter(Boolean);

  const phones = [
    { label: 'Phone', value: client.phone },
    { label: 'Phone #2', value: client.phone2 },
  ].filter(p => p.value);

  // One invoice, read the way it was written: the client, then every item on
  // it with what it covered, then what it comes to. The same shape as the
  // estimate on purpose — a bill that looks like a different document from the
  // price they agreed to is a bill that gets questioned.
  if (openInvoice) {
    const inv = openInvoice;
    const items = inv.lines.filter(l => !isMoneyLine(l));
    const adjustments = inv.lines.filter(isMoneyLine);
    const subtotal = items.reduce((t, l) => t + (Number(l.amount) || 0), 0);

    return (
      <div className="cp">
        <div className="cp-bar">
          <button className="cp-bar-btn" onClick={() => setOpenInvoice(null)}>‹ Invoices</button>
          <span className="cp-bar-title">{inv.number ? `#${inv.number}` : 'Invoice'}</span>
          <span className="cp-bar-btn" style={{ visibility: 'hidden' }}>‹</span>
        </div>
        <div className="cp-body cp-doc">
          <div className="de-sec">Client</div>
          <div className="de-client">
            <div className="de-client-name">{client.name}</div>
            <div className="de-client-sub">
              {[inv.project.title, inv.date ? fmtDay(inv.date) : ''].filter(Boolean).join(' · ')}
            </div>
          </div>

          <div className="de-sec de-sec-split">
            <span>Description</span>
            <span className="de-sec-total">{fmtMoney(inv.total)}</span>
          </div>

          {items.map((l, i) => (
            <div className="de-line" key={l._id || i} style={{ cursor: 'default' }}>
              <div className="de-line-main">
                <div className="de-line-head"><span className="de-line-name">{l.trade}</span></div>
                {l.notes && <div className="de-line-desc">{l.notes}</div>}
              </div>
              <div className="de-line-right">
                <span className="de-line-amt">{fmtMoney(l.amount)}</span>
              </div>
            </div>
          ))}

          {adjustments.length > 0 && (
            <>
              <div className="de-sum"><span>Subtotal</span><span>{fmtMoney(subtotal)}</span></div>
              {adjustments.map((l, i) => (
                <div className="de-sum" key={l._id || `adj${i}`}>
                  <span>{l.trade}</span>
                  {/* A discount reads as money coming off — "−$50.00", not the
                      "$-50.00" a plain money format would print. */}
                  <span>{l.amount < 0 ? `−${fmtMoney(Math.abs(l.amount))}` : fmtMoney(l.amount)}</span>
                </div>
              ))}
            </>
          )}

          <div className="de-total"><span>Total</span><span>{fmtMoney(inv.total)}</span></div>

          <div className="cp-foot">
            {/* The bill as a sheet of paper — the customer's copy, printed or
                saved as a PDF through the browser's own dialog. */}
            <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }}
              onClick={() => onPrintInvoice({ ...inv, client })}>
              View &amp; Print PDF
            </button>
            {/* Payments are recorded against the project statement, which is
                also what the homeowner reads in their own portal. */}
            <button className="btn btn-ghost btn-lg" onClick={() => onOpenProject(inv.project)}>
              Open {inv.project.title}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (drill) {
    const isEstimates = drill === 'estimates';
    return (
      <div className="cp">
        <div className="cp-bar">
          <button className="cp-bar-btn" onClick={() => { setDrill(null); setOpenInvoice(null); }}>‹ Profile</button>
          <span className="cp-bar-title">{client.name}</span>
          <span className="cp-bar-btn" style={{ visibility: 'hidden' }}>‹</span>
        </div>
        <div className="cp-body">
          <h1 className="cp-title">{isEstimates ? 'Estimates' : 'Invoices'}</h1>

          <button className="btn btn-primary btn-lg cp-make"
            onClick={() => (isEstimates ? onCreateEstimate(client) : onCreateInvoice(client))}>
            {isEstimates ? 'Create Estimate' : 'Create Invoice'}
          </button>

          {isEstimates ? (
            estimates.length === 0 ? (
              <div className="cp-empty">No estimate has been sent to this client yet.</div>
            ) : estimates.map((r, idx) => {
              const billed = invoicedRequestIds.has(String(r._id));
              const held = dragFrom === idx;
              const landing = dragOver === idx && dragFrom !== null && dragFrom !== idx;
              return (
                // Slide the row either way for Delete — the same gesture the
                // lead, client and vendor cards have always had.
                <SwipeToDelete key={r._id} onDelete={() => onDeleteEstimate(r)}>
                <div
                  className={`cp-card cp-rec-wrap${held ? ' cp-held' : ''}${landing ? ' cp-landing' : ''}`}
                  // Only the row whose grip is held can be dragged, so
                  // sliding a row for Delete and dragging it up the list
                  // stay two gestures that cannot be confused.
                  draggable={armed === idx}
                  onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setDragFrom(idx); }}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(idx); }}
                  onDrop={(e) => { e.preventDefault(); drop(idx); }}
                  onDragEnd={() => { setArmed(null); setDragFrom(null); setDragOver(null); }}
                >
                  <button className="cp-rec cp-rec-open" onClick={() => onOpenEstimate(r)}>
                    <div>
                      <div className="cp-rec-t">{r.items?.[0]?.title || r.serviceType || 'Estimate'}</div>
                      <div className="cp-rec-s">
                        {r.estimate?.submittedAt ? fmtDate(r.estimate.submittedAt) : 'Not sent'}
                        {r.workOrder?.number ? ` · WO ${r.workOrder.number}` : ''}
                      </div>
                    </div>
                    <div className="cp-rec-right">
                      <div className="cp-rec-amt">{fmtMoney(r.estimate?.amount)}</div>
                      {statusTag(r.status)}
                    </div>
                  </button>
                  {/* Joist's INVOICE button. The price they agreed to becomes
                      the bill, line for line, without retyping it. */}
                  <div className="cp-rec-acts">
                    {billed ? (
                      <span className="tag tag-green">Invoiced</span>
                    ) : (
                      <button className="btn btn-outline btn-sm" onClick={() => onInvoiceEstimate(client, r)}>
                        Invoice
                      </button>
                    )}
                    {/* ── The grip ──
                        Three lines, on the right, exactly where Julian asked
                        for them. Holding this is what makes the row
                        draggable; everywhere else on the row still opens the
                        estimate or slides it for Delete.

                        The arrows beside it are not decoration. A drag needs
                        a pointer that can hover, and this list is read on a
                        phone as often as on the desk. */}
                    <span className="cp-grip-wrap">
                      <button
                        type="button" className="cp-up" aria-label="Move up"
                        disabled={idx === 0}
                        onClick={() => save(moveTo(estimates, idx, idx - 1))}
                      >↑</button>
                      <span
                        className="cp-grip" role="button" tabIndex={0} aria-label="Drag to reorder"
                        onPointerDown={() => setArmed(idx)}
                        // Let go without dragging and nothing stays armed.
                        // A real drag suppresses this, so it only ever fires
                        // on a press that went nowhere.
                        onPointerUp={() => setArmed(null)}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowUp' && idx > 0) { e.preventDefault(); save(moveTo(estimates, idx, idx - 1)); }
                          if (e.key === 'ArrowDown' && idx < estimates.length - 1) { e.preventDefault(); save(moveTo(estimates, idx, idx + 1)); }
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                          <path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        </svg>
                      </span>
                      <button
                        type="button" className="cp-up" aria-label="Move down"
                        disabled={idx === estimates.length - 1}
                        onClick={() => save(moveTo(estimates, idx, idx + 1))}
                      >↓</button>
                    </span>
                  </div>
                </div>
                </SwipeToDelete>
              );
            })
          ) : (
            invoices.length === 0 ? (
              <div className="cp-empty">
                Nothing has been invoiced to this client yet.
              </div>
            ) : invoices.map(inv => (inv.kind === 'job' ? (
              // A bill raised on the job itself. It opens the job's own
              // document, which is the thing that was actually sent to the
              // customer, rather than the project reader that has no project
              // to read.
              <button key={inv.key} className="cp-card cp-rec" onClick={() => onOpenJobInvoice?.(inv.request)}>
                <div>
                  <div className="cp-rec-t">{inv.number ? `#${inv.number}` : inv.title}</div>
                  <div className="cp-rec-s">
                    {inv.date ? fmtDay(inv.date) : 'No date'}
                    {` · ${inv.items} item${inv.items === 1 ? '' : 's'}`}
                    {inv.number ? ` · ${inv.title}` : ''}
                  </div>
                </div>
                <div className="cp-rec-right">
                  <div className="cp-rec-amt">{fmtMoney(inv.total)}</div>
                  {statusTag(inv.status)}
                </div>
              </button>
            ) : (
              <SwipeToDelete key={inv.key} onDelete={() => onDeleteInvoice({ ...inv, client })}>
              <button className="cp-card cp-rec" onClick={() => setOpenInvoice(inv)}>
                <div>
                  <div className="cp-rec-t">{inv.number ? `#${inv.number}` : inv.project.title}</div>
                  <div className="cp-rec-s">
                    {inv.date ? fmtDay(inv.date) : 'No date'}
                    {` · ${inv.items} item${inv.items === 1 ? '' : 's'}`}
                    {inv.number ? ` · ${inv.project.title}` : ''}
                  </div>
                </div>
                <div className="cp-rec-right">
                  <div className="cp-rec-amt">{fmtMoney(inv.total)}</div>
                  {/* Paid or not. The list showed every invoice and said
                      nothing about any of them, so the settled ones and the
                      ones still owing read exactly alike. */}
                  {inv.settled && (
                    inv.owed <= 0.004
                      ? <span className="tag tag-green">Paid</span>
                      : inv.paid > 0.004
                        ? <span className="tag tag-amber">{fmtMoney(inv.owed)} left</span>
                        : <span className="tag tag-red">Unpaid</span>
                  )}
                  <span className="cp-rec-chev">›</span>
                </div>
              </button>
              </SwipeToDelete>
            )))
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="cp">
      <div className="cp-bar">
        <button className="cp-bar-btn" onClick={onBack}>‹ Clients</button>
        <div className="cp-bar-right">
          {/* A repeat customer rings about the next job while you have their
              page open — this starts that lead with everything already filled
              in from the record you are looking at. */}
          <button className="cp-bar-icon" aria-label="New lead for this client" onClick={() => onAddLead(client)}>
            <IconPlus size={22} />
          </button>
          <button className="cp-bar-btn" onClick={() => onEdit(client)}>Edit</button>
        </div>
      </div>

      <div className="cp-body">
        <h1 className="cp-title">Profile</h1>

        <div className="cp-card">
          <div className="cp-name">{client.name}</div>

          <div className="cp-line">
            <div className="cp-line-main">
              <div className="cp-line-lbl">Email</div>
              <div className={`cp-line-val${client.email ? '' : ' empty'}`}>{client.email || 'None on file'}</div>
            </div>
            {client.email && (
              <a className="cp-act" href={`mailto:${client.email}`} aria-label="Email this client"><IconMail /></a>
            )}
          </div>

          {phones.length === 0 ? (
            <div className="cp-line">
              <div className="cp-line-main">
                <div className="cp-line-lbl">Phone</div>
                <div className="cp-line-val empty">None on file</div>
              </div>
            </div>
          ) : phones.map(p => (
            <div className="cp-line" key={p.label}>
              <div className="cp-line-main">
                <div className="cp-line-lbl">{p.label}</div>
                <div className="cp-line-val">{p.value}</div>
              </div>
              <a className="cp-act" href={`tel:${p.value}`} aria-label={`Call ${p.value}`}><IconPhone /></a>
              <a className="cp-act" href={`sms:${p.value}`} aria-label={`Text ${p.value}`}><IconMessage /></a>
            </div>
          ))}
        </div>

        <div className="cp-card">
          <div className="cp-line-lbl">Address</div>
          {serviceAddress || serviceCityLine ? (
            <>
              {serviceAddress && <div className="cp-addr">{serviceAddress}</div>}
              {serviceCityLine && <div className="cp-addr">{serviceCityLine}</div>}
            </>
          ) : (
            <div className="cp-line-val empty">None on file</div>
          )}
        </div>

        <div className="cp-h2">
          Customer Snapshots
          <button className="cp-help" aria-label="What these figures mean" onClick={() => setHelping(h => !h)}>?</button>
        </div>
        {helping && (
          <div className="cp-help-note">
            What this client has been billed across their projects, and what has come back. Estimates are not
            money — a price becomes invoiced when it goes on a project as a line.
          </div>
        )}

        {/* What has been PAID, between the two figures it sits between. The
            row said billed and outstanding and left the middle one out, so
            a client who had paid $51,141.05 of a $52,456.44 basement showed
            nothing for it anywhere on their page. */}
        <Snapshot cells={[
          { value: fmtMoney(invoiced), label: 'Total invoiced' },
          { value: fmtMoney(received), label: 'Total paid', tone: received > 0 ? 'var(--green)' : undefined },
          { value: fmtMoney(Math.max(0, invoiced - received)), label: 'Total unpaid invoices',
            tone: invoiced - received > 0.004 ? 'var(--red)' : undefined },
        ]} />
        <Snapshot cells={[
          { value: lastInvoice ? fmtDay(lastInvoice) : '––', label: 'Last invoice sent' },
          { value: lastPayment ? fmtDay(lastPayment) : '––', label: 'Last payment received' },
        ]} />

        {/* ── A CLIENT WHO LOOKS LIKE A COMPANY ────────────────────────
            Julian's rule: a property manager starts as an ordinary client,
            and once it is clear they look after several buildings the
            customer is really the company — each property with its own work
            orders. The signal is cheap and honest: more than one address on
            their work. The decision is still his. */}
        {onPromote && addresses.length > 1 && client.role !== 'manager' && (
          <div className="alert alert-amber" style={{ margin: '0 0 16px', flexDirection: 'column', alignItems: 'flex-start' }}>
            <strong>Work at {addresses.length} different addresses.</strong>
            <span style={{ fontSize: 12.5, lineHeight: 1.55 }}>
              If they manage these rather than own them, a commercial account suits them better:
              one company, a property each with its own work orders, and their statements in a
              Billing tab of their own. Everything here moves across.
            </span>
            <button type="button" className="btn btn-outline btn-sm" style={{ marginTop: 10 }}
              onClick={() => onPromote(client, addresses.length)}>
              Turn into a commercial account
            </button>
          </div>
        )}

        {/* ── JOBS BILLED OUTSIDE A PROJECT ────────────────────────────
            A lead that gets invoiced on the Leads board raises a bill on
            the JOB, not on a project, so it never appeared on any
            statement. Julian found a $92.19 repair for Sam Skanchy sitting
            in the Invoiced column with nowhere else to read it.

            It was never missing — it is in his totals above and in his
            Invoices list — but a figure you can only find by subtracting
            two others is not on the screen. Here it is, as a row, with
            what is owed on it. */}
        {jobBills.length > 0 && (
          <div className="cp-stmt">
            <div className="cp-h2">Jobs billed on their own</div>
            <div className="st-wrap">
              <table className="st">
                <thead>
                  <tr className="st-heads">
                    <th className="g1">Date</th>
                    <th className="g1">Job</th>
                    <th className="g2">Inv #</th>
                    <th className="g2 n">Amount</th>
                    <th className="g3 n">Paid</th>
                    <th className="g4 n">Owed</th>
                    <th className="g4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Owing first, then paid, the same rule the statement
                      keeps. A row turning green drops back on its own. */}
                  {[...jobBills.filter(b => b.owed > 0.004), ...jobBills.filter(b => !(b.owed > 0.004))]
                    .map((b, i, all) => (
                      <tr
                        key={b.key}
                        className={`${b.owed > 0.004 ? 'due' : 'ok'}${
                          i === all.filter(x => x.owed > 0.004).length - 1 ? ' lastdue' : ''}`}
                        onClick={() => onOpenJobInvoice?.(b.request)}
                        style={{ cursor: onOpenJobInvoice ? 'pointer' : 'default' }}
                      >
                        <td className="g1 dim">{b.date ? fmtDay(b.date) : ''}</td>
                        <td className="g1 st-desc" title={b.title}>{b.title}</td>
                        <td className="g2">{b.number ? `#${b.number}` : '—'}</td>
                        <td className="g2 n">{fmtMoney(b.total)}</td>
                        <td className="g3 n">{fmtMoney(b.paid)}</td>
                        <td className="g4 n bal">{fmtMoney(b.owed)}</td>
                        <td className="g4 st-status">
                          {b.owed > 0.004
                            ? (b.paid > 0.004 ? 'Part Paid' : 'With Due Balance')
                            : 'Fully Paid'}
                        </td>
                      </tr>
                    ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="g1" />
                    <td className="g1">{jobBills.length} {jobBills.length === 1 ? 'job' : 'jobs'}</td>
                    <td className="g2" />
                    <td className="g2 n">{fmtMoney(sum(jobBills, b => b.total))}</td>
                    <td className="g3 n">{fmtMoney(sum(jobBills, b => b.paid))}</td>
                    <td className="g4 n bal">{fmtMoney(sum(jobBills, b => b.owed))}</td>
                    <td className="g4" />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* ── THE STATEMENT, HERE AS WELL ──────────────────────────────
            The figures above say what this client has been billed and what
            is left. The statement says WHICH line is left, and it is the
            thing Julian reads with the homeowner sitting next to him. It
            lived on one screen, two taps away through Projects. It reads
            here now, where their name is.

            Read only on this page. Editing happens on the project, where
            the lines, the payments and the expenses are in one place. */}
        {theirProjects.map(p => (
          <div key={p._id} className="cp-stmt">
            <div className="cp-h2 cp-stmt-h">
              <button type="button" className="cp-stmt-open" onClick={() => onOpenProject && onOpenProject(p)}>
                {p.title}
                <span aria-hidden="true"> ›</span>
              </button>
            </div>
            <ProjectStatement project={p} isAdmin
              onOpenInvoice={onOpenInvoice ? (line) => onOpenInvoice(p, line) : undefined} />
          </div>
        ))}

        <div className="cp-card cp-nav">
          <button className="cp-nav-row" onClick={() => setDrill('estimates')}>
            <span>Estimates</span>
            <span className="cp-nav-right"><span className="cp-count">{estimates.length}</span><IconChevron /></span>
          </button>
          <button className="cp-nav-row" onClick={() => setDrill('invoices')}>
            <span>Invoices</span>
            {/* The number of BILLS, not the number of lines on them. This read
                lines.length, so a two-item invoice said "Invoices 2" and a
                technician's bill — which has no project lines at all — said
                zero while the list below it showed the invoice. */}
            <span className="cp-nav-right"><span className="cp-count">{invoices.length}</span><IconChevron /></span>
          </button>
        </div>

        <div className="cp-make-row">
          <button className="btn btn-primary btn-lg" onClick={() => onCreateEstimate(client)}>Create Estimate</button>
          <button className="btn btn-ghost btn-lg" onClick={() => onCreateInvoice(client)}>Create Invoice</button>
        </div>

        {/* Joist's own switch, and worth keeping: the billing address, the gate
            code and whether they can get into a portal are things you need
            twice a year, and they push the money off the screen the rest of
            the time. */}
        <label className="cp-extra">
          <span>
            <span className="cp-extra-t">Show Additional Information</span>
            <span className="cp-extra-s">See important details about how you work with this customer.</span>
          </span>
          <span className={`dopt-switch${extra ? ' on' : ''}`}>
            <input
              type="checkbox"
              checked={extra}
              onChange={e => setExtra(e.target.checked)}
              // Visually hidden rather than display:none — Safari will not
              // toggle a display:none control from a label.
              style={{ position: 'absolute', opacity: 0, width: 1, height: 1 }}
            />
            <span className="dopt-knob"></span>
          </span>
        </label>

        {extra && (
          <div className="cp-card">
            <div className="cp-line">
              <div className="cp-line-main">
                <div className="cp-line-lbl">Billing address</div>
                {billingLines.length
                  ? billingLines.map((l, i) => <div className="cp-addr" key={i}>{l}</div>)
                  : <div className="cp-line-val">Same as the service address</div>}
              </div>
            </div>
            <div className="cp-line">
              <div className="cp-line-main">
                <div className="cp-line-lbl">Portal access</div>
                <div className="cp-line-val">
                  {client.email
                    ? 'They can sign in with their email'
                    : 'No email on file, so there is nowhere to send a set-password link'}
                </div>
              </div>
            </div>
            <div className="cp-line">
              <div className="cp-line-main">
                <div className="cp-line-lbl">Jobs on file</div>
                <div className="cp-line-val">
                  {theirRequests.length} {theirRequests.length === 1 ? 'request' : 'requests'}
                  {theirProjects.length ? ` · ${theirProjects.length} project${theirProjects.length === 1 ? '' : 's'}` : ''}
                </div>
              </div>
            </div>
            <div className="cp-line">
              <div className="cp-line-main">
                <div className="cp-line-lbl">Client since</div>
                <div className="cp-line-val">{client.createdAt ? fmtDate(client.createdAt) : '—'}</div>
              </div>
            </div>
            <div className="cp-line">
              <div className="cp-line-main">
                <div className="cp-line-lbl">Notes</div>
                <div className={`cp-line-val${client.notes ? '' : ' empty'}`}>{client.notes || 'None'}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
